import React, {useCallback, useRef} from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {B62_PAGE} from '@/pages/components/audit-program/b-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface B62EvaluationViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.75rem',
    py: 0.75,
    px: 0.5,
    lineHeight: 1.25,
};
const bodySx = {
    ...cellBorder,
    py: 0.5,
    px: 0.5,
    verticalAlign: 'top' as const,
    overflow: 'hidden',
};

const nativeBaseSx: SxProps<Theme> = {
    display: 'block',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 0.75,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.25,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

function CellInput({
    value,
    onChange,
    multiline,
    align = 'left',
    rows = 2,
    placeholder,
}: {
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    align?: 'left' | 'center';
    rows?: number;
    placeholder?: string;
}) {
    if (multiline) {
        return (
            <Box
                component="textarea"
                value={value}
                rows={rows}
                placeholder={placeholder}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
                sx={{...nativeBaseSx, resize: 'vertical', textAlign: align}}
            />
        );
    }
    return (
        <Box
            component="input"
            value={value}
            placeholder={placeholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{...nativeBaseSx, textAlign: align}}
        />
    );
}

/**
 * Excel B6.2 — Evaluation of Accounting Estimates.
 * Columns: # | Evaluation procedures | Estimate 1 | Estimate 2 | Estimate 3
 * Estimate headers are editable (Insert description and ref to C4.1).
 */
export default function B62EvaluationView({header, values, onChange}: B62EvaluationViewProps) {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const set = useCallback((key: string, value: string) => {
        onChangeRef.current(key, value);
    }, []);

    const headerOf = (estId: string) =>
        values[`${estId}.header`] ?? B62_PAGE.headerPlaceholder;
    const cellOf = (rowId: string, estId: string) => {
        const direct = values[`${rowId}.${estId}`];
        if (direct != null && String(direct).trim() !== '') return direct;
        // migrate old checklist Comments column into first estimate
        if (estId === 'e1') return values[`${rowId}.comments`] ?? '';
        return '';
    };

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="B6.2"
                values={values}
                onChange={set}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1, mt: 0.5}}
            >
                {B62_PAGE.title}
            </Typography>
            <Typography variant="body2" sx={{mb: 1.5, lineHeight: 1.45}}>
                {B62_PAGE.intro}
            </Typography>

            <TableContainer sx={{overflowX: 'auto'}}>
                <Table
                    size="small"
                    sx={{
                        width: '100%',
                        minWidth: 960,
                        tableLayout: 'fixed',
                        borderCollapse: 'collapse',
                    }}
                >
                    <colgroup>
                        <col style={{width: '4%'}}/>
                        <col style={{width: '36%'}}/>
                        <col style={{width: '20%'}}/>
                        <col style={{width: '20%'}}/>
                        <col style={{width: '20%'}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2}/>
                            {B62_PAGE.estimates.map((est) => (
                                <TableCell key={est.id} sx={headSx}>
                                    <CellInput
                                        value={headerOf(est.id)}
                                        onChange={(v) => set(`${est.id}.header`, v)}
                                        multiline
                                        rows={2}
                                        placeholder={B62_PAGE.headerPlaceholder}
                                    />
                                </TableCell>
                            ))}
                        </TableRow>
                        <TableRow>
                            <TableCell sx={headSx} colSpan={2}>
                                {B62_PAGE.proceduresLabel}
                            </TableCell>
                            {B62_PAGE.estimates.map((est) => (
                                <TableCell key={est.id} sx={headSx}/>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {B62_PAGE.rows.map((row) => (
                            <TableRow key={row.id}>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" fontWeight={600} align="center">
                                        {row.num}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={bodySx}>
                                    <Typography variant="body2" sx={{lineHeight: 1.4}}>
                                        {row.text}
                                    </Typography>
                                </TableCell>
                                {B62_PAGE.estimates.map((est) => (
                                    <TableCell key={est.id} sx={bodySx}>
                                        <CellInput
                                            value={cellOf(row.id, est.id)}
                                            onChange={(v) => set(`${row.id}.${est.id}`, v)}
                                            multiline
                                            rows={2}
                                        />
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
